import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';

interface BrandingResponse {
  appName: string;
  logoUrl: string;
  logoSquareUrl: string;
  primaryColor: string;
  logoShape: string;
  headerTextColor?: string;
}

/**
 * Manages dynamic org branding (app name, logo, primary color).
 * Loaded after login and on app init when a session exists.
 */
@Injectable({ providedIn: 'root' })
export class BrandingService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.authAPI}/organizations`;

  readonly appName = signal('Basement');
  readonly logoUrl = signal<string | null>(null);
  readonly logoSquareUrl = signal<string | null>(null);
  readonly primaryColor = signal<string | null>(null);
  readonly logoShape = signal<'rectangular' | 'square'>('rectangular');
  readonly headerTextColor = signal<string | null>(null);

  /** URL activa según la forma seleccionada. null si no hay logo para esa forma. */
  readonly activeLogoUrl = computed(() =>
    this.logoShape() === 'square' ? this.logoSquareUrl() : this.logoUrl()
  );

  /** Loads branding for the current user's organization. */
  loadBranding(): void {
    this.http.get<BrandingResponse>(`${this.apiUrl}/my/branding`).subscribe({
      next: (resp) => {
        this.appName.set('Basement');
        this.logoUrl.set(null);
        this.logoSquareUrl.set(null);
        this.primaryColor.set(null);
        this.logoShape.set('rectangular');
        this.headerTextColor.set(null);
        document.documentElement.style.removeProperty('--theme-header-bg');
        document.documentElement.style.removeProperty('--theme-header-text');
        if (resp.appName) this.appName.set(resp.appName);
        if (resp.logoUrl) this.logoUrl.set(resp.logoUrl);
        if (resp.logoSquareUrl) this.logoSquareUrl.set(resp.logoSquareUrl);
        if (resp.headerTextColor) this.headerTextColor.set(resp.headerTextColor);
        if (resp.primaryColor) {
          this.primaryColor.set(resp.primaryColor);
          this.applyColor(resp.primaryColor);
        }
        if (resp.logoShape) this.logoShape.set(resp.logoShape as 'rectangular' | 'square');
      },
      error: () => { /* best-effort — branding is non-critical */ },
    });
  }

  /** Applies the branding color to the header background with contrast-aware text. */
  applyColor(color: string): void {
    document.documentElement.style.setProperty('--theme-header-bg', color);
    const stored = this.headerTextColor();
    document.documentElement.style.setProperty(
      '--theme-header-text',
      stored || (this.isLightColor(color) ? '#000000' : '#ffffff')
    );
  }

  /** Resets branding to defaults (on logout). */
  reset(): void {
    this.appName.set('Basement');
    this.logoUrl.set(null);
    this.logoSquareUrl.set(null);
    this.primaryColor.set(null);
    this.logoShape.set('rectangular');
    this.headerTextColor.set(null);
    document.documentElement.style.removeProperty('--theme-header-bg');
    document.documentElement.style.removeProperty('--theme-header-text');
  }

  /** Relative luminance > 0.5 means the color is perceived as light. */
  private isLightColor(hex: string): boolean {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luminance > 0.5;
  }
}
